import React from "react";
import { Box, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useBlogArticleContentSectionStyles } from "./BlogArticleContentSection.styles";
import { useI18n } from "../../../../i8n/useI18n";
import { useCommerceData } from "../../../../hooks/useCommerceData";
import { commerceRepository } from "../../../../api";
import { Product } from "../../../../api/types";
import { formatDate } from "../../../../utils/formatDate";
import ProductCard from "../../../ui/ProductCard";

interface BlogArticleContentSectionProps {
    handle: string;
}

const BlogArticleContentSection = (props: BlogArticleContentSectionProps) => {
    const { classes } = useBlogArticleContentSectionStyles();
    const { t } = useI18n();

    const { data: article, loading } = useCommerceData(
        () => commerceRepository.getArticleByHandle(props.handle),
        [props.handle]
    );

    const { data: relatedProducts } = useCommerceData(async (): Promise<Product[]> => {
        if (!article) return [];
        const results = await Promise.all(article.relatedProductHandles.map((h) => commerceRepository.getProductByHandle(h)));
        return results.filter((p): p is Product => p !== null);
    }, [article]);

    if (loading) {
        return null;
    }

    if (!article) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Typography>{t("common.error")}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            <Helmet>
                <title>{article.title} | {t("brand.name")}</title>
                <meta name="description" content={article.excerpt} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        headline: article.title,
                        description: article.excerpt,
                        image: article.image?.url,
                        datePublished: article.publishedAt,
                        author: { "@type": "Person", name: article.author.name },
                    })}
                </script>
            </Helmet>
            <Box className={classes.container}>
                <Typography component={RouterLink} to={t("links.blog")} className={classes.backLink}>
                    ← {t("blogArticlePage.backToBlog")}
                </Typography>

                <Typography className={classes.category}>{article.tags[0]}</Typography>
                <Typography variant="h1" className={classes.title}>{article.title}</Typography>
                <Typography className={classes.meta}>
                    {article.author.name} · {formatDate(article.publishedAt)} · {article.readingMinutes} {t("blogArticlePage.minuteReadLabel")}
                </Typography>

                {article.image && (
                    <Box className={classes.imageWrapper}>
                        <Box component="img" src={article.image.url} alt={article.image.altText ?? article.title} className={classes.image} />
                    </Box>
                )}

                <Box className={classes.content} dangerouslySetInnerHTML={{ __html: article.contentHtml }} />

                {relatedProducts && relatedProducts.length > 0 && (
                    <Box className={classes.relatedProducts}>
                        <Typography variant="h4" className={classes.relatedProductsTitle}>
                            {t("blogArticlePage.relatedProductsTitle")}
                        </Typography>
                        <Box className={classes.grid}>
                            {relatedProducts.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default BlogArticleContentSection;
